import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { Dropdown } from '../fields/Dropdown';
import { getShowUpdatedOnly, OpinionDropdownField } from '../store';
import styles from './OpinionSection.module.scss';
import { useAppSelector } from '../../../../hooks/react-redux';
import { ReadOnlyBlacklineField } from '../fields/ReadOnlyBlacklineField';

interface EligibleCollateralRowProps {
    fieldId: string;
    collateral: string;
    fields: OpinionDropdownField[];
    previousFields: OpinionDropdownField[] | null;
    getIsDisabled: (fieldId: string, index: number) => boolean;
    shouldShowFieldUpdated: (fieldId: string, index: number) => boolean;
    getShouldDisplayField: (fieldId: string, index: number) => boolean;
    updateField: (fieldId: string, index: number, option: string[] | null) => void;
    isReadOnly: boolean;
    isCashRow?: boolean;
}

export const EligibleCollateralRow: React.FC<EligibleCollateralRowProps> = ({ fieldId, collateral, fields, previousFields, getIsDisabled, shouldShowFieldUpdated, getShouldDisplayField, updateField, isReadOnly, isCashRow = false }) => {
    const id = `eligibleCollateral-${fieldId}`;
    const showUpdatedOnly = useAppSelector(getShowUpdatedOnly);
    const updateValue = useCallback((index: number, option: string[] | null) => updateField(fieldId, index, option), [updateField, fieldId]);

    const getPreviousValue = useCallback((index: number) => !isNull(previousFields) && previousFields[index] && previousFields[index].value || null, [previousFields]);

    const getDropdownDisplay = useCallback((field: OpinionDropdownField, index: number) => {
        const shouldDisplayField = getShouldDisplayField(fieldId, index);
        if (shouldDisplayField) {
            if (showUpdatedOnly && isReadOnly && !isNull(previousFields)) {
                const previousCellValue = getPreviousValue(index);
                const previousValue = !isNull(previousCellValue) ? previousCellValue.join(', ') : '';
                const readOnlyValue = !isNull(field.value) ? field.value.join(', ') : '';
                return (
                    <ReadOnlyBlacklineField
                        id={`${id}-${index}`}
                        value={readOnlyValue}
                        previousValue={previousValue}
                    />
                );
            }
            return (
                <Dropdown
                    dropdownList={field.dropdownList}
                    id={`${id}-${index}`}
                    updateValue={value => updateValue(index, value)}
                    value={field.value}
                    disabled={getIsDisabled(fieldId, index)}
                    showFieldUpdated={shouldShowFieldUpdated(fieldId, index)}
                    hideDropdownIndicator
                />
            );
        }
    }, [getShouldDisplayField, getPreviousValue, id, updateValue, getIsDisabled, isReadOnly, previousFields, shouldShowFieldUpdated, fieldId, showUpdatedOnly]);

    if (isCashRow) {
        return (
            <div className={styles.collateralRowWrapper}>
                <div className={styles.collateralCell}>{collateral}</div>
                {fields.map((field, index) => (
                    <div className={styles.dropdownCell} key={index}>
                        {getDropdownDisplay(field, index)}
                    </div>
                ))}
                {Array(4).fill(1).map((_, i) => (<div key={i} className={styles.emptyCell} />))}
            </div>
        );
    }

    return (
        <div className={styles.collateralRowWrapper}>
            <div className={styles.collateralCell}>{collateral}</div>
            <div className={styles.emptyCell} />
            {fields.map((field, index) => (
                <div className={styles.dropdownCell} key={index}>
                    {getDropdownDisplay(field, index)}
                </div>
            ))}
        </div>
    );
};
